import { useCallback, useEffect, useRef, useState } from "react";

/** How long a copy button says "Copied". */
export const COPIED_MS = 2000;

/**
 * Copy text to the clipboard and say "Copied" for {@link COPIED_MS}. A second
 * copy restarts the time, and the timer is cleared on unmount. A refused
 * clipboard write changes nothing.
 */
export function useCopyFeedback(): { copied: boolean; copy: (text: string) => void } {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    []
  );

  const copy = useCallback((text: string) => {
    void navigator.clipboard.writeText(text).then(
      () => {
        if (timer.current !== null) clearTimeout(timer.current);
        setCopied(true);
        timer.current = setTimeout(() => {
          timer.current = null;
          setCopied(false);
        }, COPIED_MS);
      },
      () => undefined
    );
  }, []);

  return { copied, copy };
}
